"use client";

import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[RouteError]', error);
    // Boundary-caught errors never reach window.onerror — beacon them so
    // they're visible in /api/diag alongside uncaught errors.
    try {
      (window as any).__bbSend?.('route-error', {
        message: String(error?.message || '').slice(0, 400),
        digest: error?.digest || '',
        stack: error?.stack ? String(error.stack).slice(0, 1500) : '',
      });
    } catch { /* ignore */ }
  }, [error]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center gap-6">
      <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
        <AlertTriangle className="h-8 w-8 text-destructive" />
      </div>
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground max-w-md">
          We hit an unexpected error. This has been logged and we&apos;ll look into it.
        </p>
        {error.message && (
          <p className="text-xs text-destructive/80 font-mono bg-destructive/5 rounded px-3 py-2 max-w-lg mx-auto text-left break-all">
            {error.message}
          </p>
        )}
        {error.digest && (
          <p className="text-xs text-muted-foreground/60 font-mono">
            Error ID: {error.digest}
          </p>
        )}
      </div>
      <div className="flex gap-3">
        <Button onClick={reset} variant="default" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
        <Button onClick={() => window.location.href = '/home'} variant="outline" className="gap-2">
          <Home className="h-4 w-4" />
          Go home
        </Button>
      </div>
    </div>
  );
}
